<script setup>
// Auditoria - timeline visual com filtros elegantes.
// Substitui a tabela genérica antiga por cards de evento com ícone,
// usuário, descrição contextual e expansão pra ver o diff completo.
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import SettingsPageLayout from 'dashboard/components-next/layout/SettingsPageLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import AddFilterChip from 'dashboard/routes/dashboard/settings/reports/components/Filters/v3/AddFilterChip.vue';
import ActiveFilterChip from 'dashboard/routes/dashboard/settings/reports/components/Filters/v3/ActiveFilterChip.vue';
import FilterButton from 'dashboard/components/ui/Dropdown/DropdownButton.vue';
import AuditLogsEmptyState from 'dashboard/components-next/Crm/EmptyState/AuditLogsEmptyState.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const route = useRoute();

const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const filtersMeta = computed(() => getters['auditlogs/getFiltersMeta'].value);

const filters = reactive({
  q: '',
  userId: '',
  action: '',
  auditableType: '',
  startDate: '',
  endDate: '',
  perPage: 25,
  sortDir: 'desc',
});

const expandedId = ref(null);

const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);

// Na carga inicial a lista deve mostrar TODOS os eventos (sem filtro de data,
// mais recentes primeiro). O DatePicker nasce em "Últimos 7 dias" apenas como
// rótulo visual; ele NÃO deve aplicar esse filtro automaticamente no load.
// Esta flag neutraliza qualquer emissão precoce de `date-range-changed`
// (seja por watcher imediato, clickaway ou reidratação de v-model na montagem):
// o primeiro disparo só sincroniza o estado visual e mantém o período vazio.
// A partir daí, mudanças feitas pelo usuário aplicam o filtro normalmente.
const isInitialRangeEmit = ref(true);

const toIsoDate = date => (date ? format(date, 'yyyy-MM-dd') : '');

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  filters.startDate = toIsoDate(startDate);
  filters.endDate = toIsoDate(endDate);
};

// Versão que aplica os filtros imediatamente - segue o padrão CSAT.
const onDateRangeChangeAuto = value => {
  // Primeira emissão (montagem): só espelha o range visual, sem aplicar filtro
  // de data. Mantém filters.startDate/endDate vazios para o backend trazer tudo.
  if (isInitialRangeEmit.value) {
    isInitialRangeEmit.value = false;
    const [startDate, endDate, rangeType] = value;
    customDateRange.value = [startDate, endDate];
    selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
    return;
  }
  onDateRangeChange(value);
  applyFilters();
};

const ACTION_META = {
  sign_in: {
    icon: 'i-lucide-log-in',
    color: 'text-n-teal-11',
    bg: 'bg-n-teal-3',
    labelKey: 'AUDIT_LOGS.ACTIONS.SIGN_IN',
  },
  sign_out: {
    icon: 'i-lucide-log-out',
    color: 'text-n-slate-11',
    bg: 'bg-n-alpha-2',
    labelKey: 'AUDIT_LOGS.ACTIONS.SIGN_OUT',
  },
  create: {
    icon: 'i-lucide-plus-circle',
    color: 'text-n-teal-11',
    bg: 'bg-n-teal-3',
    labelKey: 'AUDIT_LOGS.ACTIONS.CREATE',
  },
  update: {
    icon: 'i-lucide-pen-square',
    color: 'text-n-blue-11',
    bg: 'bg-n-blue-3',
    labelKey: 'AUDIT_LOGS.ACTIONS.UPDATE',
  },
  destroy: {
    icon: 'i-lucide-trash-2',
    color: 'text-n-ruby-11',
    bg: 'bg-n-ruby-3',
    labelKey: 'AUDIT_LOGS.ACTIONS.DESTROY',
  },
};

const actionDescriptor = action => {
  if (!action) return ACTION_META.update;
  if (action.startsWith('status:')) {
    return {
      icon: 'i-lucide-circle-dot',
      color: 'text-n-amber-11',
      bg: 'bg-n-amber-3',
      labelKey: null,
    };
  }
  return ACTION_META[action] || ACTION_META.update;
};

const buildParams = page => ({
  page: page || 1,
  perPage: filters.perPage,
  sortDir: filters.sortDir,
  q: filters.q || undefined,
  userId: filters.userId || undefined,
  action: filters.action || undefined,
  auditableType: filters.auditableType || undefined,
  startDate: filters.startDate || undefined,
  endDate: filters.endDate || undefined,
});

const fetchAuditLogs = (page = 1) => {
  store
    .dispatch('auditlogs/fetch', buildParams(page))
    .catch(error =>
      useAlert(error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE'))
    );
};

const applyFilters = () => {
  router.push({ name: 'auditlogs_list', query: { page: 1 } });
  fetchAuditLogs(1);
};

const exportCsv = () => {
  store.dispatch('auditlogs/exportCsv', buildParams());
};

const toggleExpand = id => {
  expandedId.value = expandedId.value === id ? null : id;
};

const formatChanges = changes => {
  if (
    !changes ||
    (typeof changes === 'object' && Object.keys(changes).length === 0)
  ) {
    return t('AUDIT_LOGS.LIST.NO_CHANGES');
  }
  return JSON.stringify(changes, null, 2);
};

const formatTimestamp = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
};

const auditableLabel = item => {
  if (!item.auditable_type) return '-';
  return item.auditable_type.split('::').pop();
};

const activityText = item => {
  const descriptor = actionDescriptor(item.action);
  if (descriptor.labelKey) {
    return t(descriptor.labelKey, {
      resource: auditableLabel(item),
      id: item.auditable_id,
    });
  }
  if (item.action?.startsWith('status:')) {
    return t('AUDIT_LOGS.ACTIONS.STATUS_CHANGED', {
      resource: auditableLabel(item),
      status: item.action.split(':')[1],
    });
  }
  return `${item.action} • ${auditableLabel(item)}`;
};


// Filtros no padrão "Adicionar filtro" da plataforma (mesmo dos Relatórios
// SLA): chip dropdown sob demanda em vez de selects fixos esticados.
// Tipos disponíveis: Usuário e Ação. `key` mapeia pro estado `filters`.
const FILTER_DEFS = [
  { type: 'users', name: t('AUDIT_LOGS.FILTERS.USER'), key: 'userId' },
  { type: 'actions', name: t('AUDIT_LOGS.FILTERS.ACTION'), key: 'action' },
];

const keyForType = type => FILTER_DEFS.find(d => d.type === type)?.key;
const isFilterActive = def => !!filters[def.key];

// Fontes de opções no formato do FilterListDropdown ({ id, name, type }).
const filterSources = computed(() => ({
  users: (filtersMeta.value.users || []).map(u => ({
    id: u.id,
    name: u.name || u.email,
    type: 'users',
  })),
  actions: (filtersMeta.value.actions || []).map(a => ({
    id: a,
    name: a,
    type: 'actions',
  })),
}));

// Tipos ainda NÃO aplicados - alimenta o menu do "Adicionar filtro".
const filterListMenuItems = computed(() =>
  FILTER_DEFS.filter(d => !isFilterActive(d)).map((d, i) => ({
    id: String(i + 1),
    name: d.name,
    type: d.type,
    options: filterSources.value[d.type],
  }))
);

// Filtros aplicados - viram chips com valor resolvido + ação de remover.
const activeFilters = computed(() =>
  FILTER_DEFS.filter(isFilterActive).map(d => {
    const val = filters[d.key];
    const opt = filterSources.value[d.type].find(
      o => String(o.id) === String(val)
    );
    return {
      id: typeof val === 'number' ? val : null,
      name: `${d.name}: ${opt?.name ?? val}`,
      type: d.type,
      options: filterSources.value[d.type],
    };
  })
);

const hasChipFilters = computed(() => FILTER_DEFS.some(isFilterActive));
const isAllFilterSelected = computed(
  () => !filterListMenuItems.value.length
);

// Estado do dropdown - espelha SLAFilter.vue.
const showDropdownMenu = ref(false);
const showSubDropdownMenu = ref(false);
const activeFilterType = ref('');

const showDropdown = () => {
  showSubDropdownMenu.value = false;
  showDropdownMenu.value = !showDropdownMenu.value;
};
const closeDropdown = () => {
  showDropdownMenu.value = false;
};
const openActiveFilterDropdown = type => {
  closeDropdown();
  activeFilterType.value = type;
  showSubDropdownMenu.value = !showSubDropdownMenu.value;
};
const closeActiveFilterDropdown = () => {
  activeFilterType.value = '';
  showSubDropdownMenu.value = false;
};
const resetDropdown = () => {
  closeDropdown();
  closeActiveFilterDropdown();
};

const addFilter = item => {
  const key = keyForType(item.type);
  if (key) filters[key] = item.id;
  applyFilters();
  resetDropdown();
};
const removeFilter = type => {
  const key = keyForType(type);
  if (key) filters[key] = '';
  applyFilters();
};
const clearChipFilters = () => {
  FILTER_DEFS.forEach(d => {
    filters[d.key] = '';
  });
  applyFilters();
  resetDropdown();
};

onMounted(() => {
  store.dispatch('auditlogs/fetchFiltersMeta');
  fetchAuditLogs(Number(route.query.page ?? 1));
});

watch(
  () => Number(route.query.page ?? 1),
  (newPage, oldPage) => {
    if (newPage !== oldPage) fetchAuditLogs(newPage);
  }
);

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { page } });
};
</script>

<template>
  <SettingsPageLayout
    :title="$t('AUDIT_LOGS.HEADER')"
    :description="$t('AUDIT_LOGS.DESCRIPTION')"
    :is-loading="uiFlags.fetchingList"
    :is-empty="!records.length"
    :show-pagination="records.length > 0"
    :current-page="Number(meta.currentPage)"
    :total-count="meta.totalEntries"
    :items-per-page="meta.perPage"
    @update:current-page="onPageChange"
  >
    <template #actions>
      <router-link
        :to="{ name: 'auditlogs_summary' }"
        class="text-body-small text-n-slate-11 hover:text-n-slate-12 inline-flex items-center gap-1.5"
      >
        <i class="i-lucide-bar-chart-3 size-3.5" />
        {{ $t('AUDIT_LOGS.SUMMARY') }}
      </router-link>
      <router-link
        :to="{ name: 'auditlogs_indicators' }"
        class="text-body-small text-n-slate-11 hover:text-n-slate-12 inline-flex items-center gap-1.5"
      >
        <i class="i-lucide-eye size-3.5" />
        {{ $t('AUDIT_LOGS.CONFIGURE_INDICATORS') }}
      </router-link>
      <NextButton
        faded
        slate
        icon="i-lucide-download"
        :is-loading="uiFlags.exporting"
        :label="$t('AUDIT_LOGS.EXPORT')"
        @click="exportCsv"
      />
    </template>

    <template #subHeader>
      <!-- DÍVIDA: migrar date picker e chips de filtro para primitivos premium de components-next (ver plano 03). -->
      <!-- Filtros sempre visíveis logo abaixo do título: período + busca + chips.
           flex respirado (gap-3, items-center) e flex-wrap só estoura no mobile estreito. -->
      <div class="flex items-center gap-3 flex-wrap pb-4">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          show-range
          @date-range-changed="onDateRangeChangeAuto"
        />

        <div class="relative flex-1 min-w-[16rem] max-w-md">
          <Input
            :model-value="filters.q"
            type="search"
            :placeholder="$t('AUDIT_LOGS.FILTERS.SEARCH')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="filters.q = $event.target.value"
            @enter="applyFilters"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>

        <div
          class="flex flex-col flex-wrap items-start gap-2 md:items-center md:flex-nowrap md:flex-row"
        >
          <div
            v-if="hasChipFilters"
            class="flex flex-wrap gap-2 md:flex-nowrap"
          >
            <ActiveFilterChip
              v-for="filter in activeFilters"
              v-bind="filter"
              :key="filter.type"
              :placeholder="
                $t(`AUDIT_LOGS.FILTERS.${filter.type.toUpperCase()}`)
              "
              :active-filter-type="activeFilterType"
              :show-menu="showSubDropdownMenu"
              enable-search
              @toggle-dropdown="openActiveFilterDropdown"
              @close-dropdown="closeActiveFilterDropdown"
              @add-filter="addFilter"
              @remove-filter="removeFilter"
            />
          </div>
          <div
            v-if="hasChipFilters && !isAllFilterSelected"
            class="w-full h-px border md:w-px md:h-5 border-n-weak"
          />
          <div class="flex items-center gap-2">
            <AddFilterChip
              v-if="!isAllFilterSelected"
              placeholder-i18n-key="AUDIT_LOGS.FILTERS"
              :name="$t('AUDIT_LOGS.FILTERS.ADD_FILTER')"
              :menu-option="filterListMenuItems"
              :show-menu="showDropdownMenu"
              :empty-state-message="$t('AUDIT_LOGS.FILTERS.NO_FILTER')"
              @toggle-dropdown="showDropdown"
              @close-dropdown="closeDropdown"
              @add-filter="addFilter"
            />
            <div
              v-if="hasChipFilters"
              class="w-px h-5 border border-n-weak"
            />
            <FilterButton
              v-if="hasChipFilters"
              :button-text="$t('AUDIT_LOGS.FILTERS.CLEAR_ALL')"
              @click="clearChipFilters"
            />
          </div>
        </div>
      </div>
    </template>

    <template #emptyState>
      <!-- Empty state - padrão EmptyStateLayout com cards-fantasma de eventos. -->
      <div class="min-h-[28rem] h-[28rem]">
        <AuditLogsEmptyState
          :has-active-filters="
            !!(
              filters.q ||
              filters.userId ||
              filters.action ||
              filters.auditableType ||
              filters.startDate ||
              filters.endDate
            )
          "
        />
      </div>
    </template>

    <template #body>
      <!-- Timeline de eventos - cards com ícone, usuário, descrição e expansão do diff. -->
      <ol class="flex flex-col gap-2 pb-4">
        <li
          v-for="item in records"
          :key="item.id"
          class="rounded-xl border border-n-weak hover:border-n-slate-7 transition-colors"
        >
          <button
            class="w-full px-4 py-3 flex items-start gap-3 text-left"
            @click="toggleExpand(item.id)"
          >
            <span
              class="size-9 rounded-full flex items-center justify-center flex-shrink-0"
              :class="actionDescriptor(item.action).bg"
            >
              <i
                class="size-4"
                :class="[
                  actionDescriptor(item.action).icon,
                  actionDescriptor(item.action).color,
                ]"
              />
            </span>
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-3">
                <p class="text-body-main font-medium text-n-slate-12 truncate">
                  {{ item.comment || activityText(item) }}
                </p>
                <span class="text-body-small text-n-slate-10 flex-shrink-0">
                  {{ formatTimestamp(item.created_at) }}
                </span>
              </div>
              <div
                class="flex items-center gap-3 mt-1 text-body-small text-n-slate-11"
              >
                <span
                  v-if="item.username"
                  class="inline-flex items-center gap-1"
                >
                  <i class="i-lucide-user size-3" />
                  {{ item.username }}
                </span>
                <span
                  v-if="item.remote_address"
                  class="inline-flex items-center gap-1"
                >
                  <i class="i-lucide-network size-3" />
                  {{ item.remote_address }}
                </span>
                <span
                  v-if="item.auditable_type"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-n-alpha-1 font-mono text-label-small"
                >
                  {{ auditableLabel(item) }}#{{ item.auditable_id }}
                </span>
              </div>
            </div>
            <i
              class="size-4 text-n-slate-10 flex-shrink-0 mt-1"
              :class="
                expandedId === item.id
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
            />
          </button>
          <div v-if="expandedId === item.id" class="px-4 pb-4 -mt-1">
            <pre
              class="text-body-small bg-n-alpha-1 rounded-md p-3 overflow-x-auto whitespace-pre-wrap text-n-slate-12 font-mono"
              >{{ formatChanges(item.audited_changes) }}</pre
            >
          </div>
        </li>
      </ol>
    </template>
  </SettingsPageLayout>
</template>
